import { CartUpdatedEvent, ThemeEvents } from '@theme/events';

class QuickAddComponent extends HTMLElement {
  #abortController = null;
  #parentSliderComponent = null;

  constructor() {
    super();

    this.addToCartButton = null;
    this.popupComponent = null;
    this.popupModal = null;
    this.popupToggle = null;
    this.popupModalContent = null;
    this.showProductMediaGallery = null;
  }

  connectedCallback() {
    this.addToCartButton = this.querySelector('[data-ref="add-to-cart-button"]');
    this.popupComponent = this.querySelector('[data-ref="popup-component"]');
    this.popupModal = this.popupComponent?.querySelector('[data-ref="popup-modal"]');
    this.popupToggle = this.popupComponent?.querySelector('[data-ref="popup-toggle"]');
    this.popupModalContent = this.popupComponent?.querySelector('[data-ref="popup-content"]');
    this.showProductMediaGallery = this.popupComponent?.dataset.showProductMediaGallery;
    this.#parentSliderComponent = this.closest('slider-component');

    this.addToCartButton?.addEventListener('click', this.#handleAddToCart);
    this.popupToggle?.addEventListener('click', this.#handlePopupToggle);
    this.popupModal?.addEventListener('close', this.#handlePopupClose);
    this.popupModalContent?.addEventListener(ThemeEvents.variantUpdated, this.#handlePopupVariantUpdated);

    document.addEventListener(ThemeEvents.cartUpdated, () => this.#handleCartUpdate());
  }

  disconnectedCallback() {
    this.addToCartButton?.removeEventListener('click', this.#handleAddToCart);
    this.popupToggle?.removeEventListener('click', this.#handlePopupToggle);
    this.popupModal?.removeEventListener('close', this.#handlePopupClose);
    this.popupModalContent?.removeEventListener(ThemeEvents.variantUpdated, this.#handlePopupVariantUpdated);

    document.removeEventListener(ThemeEvents.cartUpdated, () => this.#handleCartUpdate());
  }

  #handleAddToCart = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.addToCartButton.classList.add('is-loading');

    const isPackBuilderMode = this.addToCartButton?.dataset?.packBuilderMode === 'true';
    if (isPackBuilderMode) {
      await this.#dispatchPackBuilderVariant('add-button');
      this.addToCartButton.classList.remove('is-loading');
      return;
    }

    let items = [];

    items.push({
      id: this.addToCartButton.dataset.variantId,
      quantity: 1,
    });

    const sections = [];
    if (document.querySelector('cart-drawer')) {
      const section_cart_drawer_id = document.querySelector('cart-drawer').dataset.sectionId;
      sections.push(section_cart_drawer_id);
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items,
          sections: sections.join(','),
        }),
      });

      const data = await response.json();

      this.updateCart(data, items.length);

      this.addToCartButton.classList.remove('is-loading');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  #dispatchPackBuilderVariant = async (source = 'add-button') => {
    const trigger = source === 'popup-toggle' ? this.popupToggle : this.addToCartButton;
    const variantId = trigger?.dataset?.variantId || this.addToCartButton?.dataset?.variantId;
    return this.#dispatchPackBuilderVariantById(variantId);
  };

  #dispatchPackBuilderVariantById = async (variantId) => {
    if (!variantId) return;

    const productCard = this.closest('product-card');
    const productUrl = this.popupToggle?.dataset?.productPageUrl || productCard?.dataset?.productUrl;
    if (!productUrl) return;

    const cleanPath = productUrl.split('?')[0].split('#')[0];
    const parts = cleanPath.split('/').filter(Boolean);
    const productHandle = parts[parts.length - 1];
    if (!productHandle) return;

    try {
      const response = await fetch(`/products/${productHandle}.js`);
      const data = await response.json();
      const variant = data.variants.find((item) => item.id === parseInt(variantId, 10));
      if (!variant) return;

      if (!variant.featured_image) {
        variant.featured_image = {
          src: data.featured_image || '',
          alt: data.title,
        };
      }

      variant.name = data.title;
      document.dispatchEvent(
        new CustomEvent('pack-builder-selected-variants-changed', {
          detail: variant,
        }),
      );
    } catch (error) {
      console.error('Error:', error);
    }
  };

  updateCart(data, itemsAdded) {
    if (window.location.pathname.includes('/cart')) {
      window.location.reload();
    } else {
      const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();

      let newItemCount;
      if (currentItemCount != null) {
        newItemCount = currentItemCount + itemsAdded;
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: newItemCount,
            source: 'quick-add',
            sections: data.sections,
          },
        ),
      );
    }
  }

  #handlePopupToggle = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    await this.#openPopup();
  };

  #openPopup = async () => {
    const isPackBuilderMode = this.popupToggle?.dataset?.packBuilderMode === 'true';
    const shouldOpenPopupInPackBuilder = this.popupToggle?.dataset?.openPopupInPackBuilder === 'true';

    if (isPackBuilderMode && !shouldOpenPopupInPackBuilder) {
      this.popupToggle?.classList.add('is-loading');
      await this.#dispatchPackBuilderVariant('popup-toggle');
      this.popupToggle?.classList.remove('is-loading');
      return;
    }

    this.popupModalContent.classList.add('is-loading');

    this.#disableParentSliderDrag();

    const productPageHtml = await this.fetchProductPage(this.popupToggle.dataset.productPageUrl);

    if (productPageHtml) {
      let newContent = (this.popupModalContent.innerHTML = productPageHtml.querySelector('[data-ref="product-section-content"]').innerHTML);
      if (this.showProductMediaGallery === 'false') {
        newContent = this.popupModalContent.innerHTML = productPageHtml.querySelector('[data-ref="product-section-product-info"]').innerHTML;
      }
      this.popupModalContent.innerHTML = newContent;
    }

    // Notifier le product-form que le contenu est chargé pour déclencher la mise à jour des prix
    const productForm = this.popupModalContent.querySelector('[data-ref="product-form"]');
    productForm?.dispatchEvent(new CustomEvent('quickAddContentLoaded'));
    window.__runTextSeparatorCleaner?.();

    this.popupModalContent.classList.remove('is-loading');
  };

  #handlePopupVariantUpdated = async (event) => {
    if (this.dataset.packBuilderPopupMode !== 'true') return;
    if (!this.popupModal?.open) return;

    const variantId = event.detail?.resource?.id;
    if (!variantId) return;

    await this.#dispatchPackBuilderVariantById(variantId);
    this.popupComponent?.close();
  };

  #handlePopupClose = () => {
    this.#enableParentSliderDrag();
  };

  #disableParentSliderDrag() {
    if (!this.#parentSliderComponent?.slider) return;
    this.#parentSliderComponent.slider.options = { drag: false };
  }

  #enableParentSliderDrag() {
    if (!this.#parentSliderComponent?.slider) return;
    this.#parentSliderComponent.slider.options = { drag: true };
  }

  #handleCartUpdate = () => {
    this.popupComponent?.close();
  };

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}
